import React, { useState, useRef, useEffect } from 'react'
import { Play, Pause, Volume2, VolumeX, Maximize, RotateCcw, AlertCircle, List, CheckCircle } from 'lucide-react'

interface VideoItem {
    title: string
    description: string
    videoPath: string
}

interface VideoSimulationProps {
    title?: string
    description?: string
    videoPath?: string
    videos?: VideoItem[]
    onProgress?: (progress: number) => void
    showInfo?: boolean
    compact?: boolean
}

const VideoSimulation: React.FC<VideoSimulationProps> = ({
    title = "Mô phỏng bằng video",
    description = "Xem video mô phỏng để hiểu rõ hơn về nội dung bài học",
    videoPath,
    videos = [],
    onProgress,
    showInfo = true,
    compact = false
}) => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isMuted, setIsMuted] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [error, setError] = useState(false)
    const [loading, setLoading] = useState(true)
    const [isVisible, setIsVisible] = useState(false)
    // Ref for the container to track visibility
    const containerRef = useRef<HTMLDivElement>(null)

    // Determine active video list
    const videoList = videos.length > 0 ? videos : (videoPath ? [{ title, description, videoPath }] : [])
    const [currentIndex, setCurrentIndex] = useState(0)

    // Get current active video
    const activeVideo = videoList[currentIndex] || { title, description, videoPath: '' }

    const [currentVideoPath, setCurrentVideoPath] = useState(activeVideo.videoPath)
    const [hasRetried, setHasRetried] = useState(false)
    const [showControls, setShowControls] = useState(false)
    const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Lazy Loading Implementation
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true)
                    observer.disconnect() // Load once, then stop observing
                }
            },
            {
                threshold: 0.1, // Trigger when 10% visible
                rootMargin: '100px' // Start loading 100px before it hits viewport
            }
        )

        if (containerRef.current) {
            observer.observe(containerRef.current)
        }

        return () => observer.disconnect()
    }, [])

    // Sync currentVideoPath when activeVideo changes (e.g. playlist click)
    useEffect(() => {
        // Only update if visible to prevent background loading of unrelated videos
        if (isVisible) {
            setCurrentVideoPath(activeVideo.videoPath)
            setHasRetried(false)
            setIsPlaying(false)
            setShowControls(true)
        }
    }, [activeVideo.videoPath, isVisible])

    // Handle controls visibility
    const handleVideoClick = () => {
        // Toggle play/pause on click
        handlePlayPause()

        // Also show controls
        setShowControls(true)
        resetControlsTimeout()
    }

    const handleControlsInteraction = () => {
        setShowControls(true)
        resetControlsTimeout()
    }

    const resetControlsTimeout = () => {
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current)
        }
        controlsTimeoutRef.current = setTimeout(() => {
            if (isPlaying) {
                setShowControls(false)
            }
        }, 3000)
    }

    useEffect(() => {
        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current)
            }
        }
    }, [])

    useEffect(() => {
        // Don't init video logic if not visible yet
        if (!isVisible) return

        const video = videoRef.current
        if (!video || !currentVideoPath) {
            if (currentVideoPath && video) {
                // Video element exists but maybe path update lag?
            } else if (currentVideoPath) {
                // Component might be unmounting or not ref'd yet
            }
            setLoading(false)
            return
        }

        console.log('📹 VideoSimulation: Loading video from path:', currentVideoPath)

        const handleLoadedMetadata = () => {
            console.log('✅ VideoSimulation: Video loaded successfully, duration:', video.duration)
            setDuration(video.duration)
            setLoading(false)
            setError(false)
            // Auto play when switching videos in playlist
            if (videos.length > 0) {
                video.play()
                    .then(() => {
                        setIsPlaying(true)
                        setShowControls(false) // Hide controls after autoplay starts
                    })
                    .catch((e) => {
                        console.log('⚠️ Autoplay prevented (likely browser policy):', e)
                        setIsPlaying(false)
                        setShowControls(true) // Show controls so user can play manually
                    })
            }
        }

        const handleTimeUpdate = () => {
            setCurrentTime(video.currentTime)
            if (onProgress && video.duration > 0) {
                onProgress(video.currentTime / video.duration)
            }
        }

        const handleEnded = () => {
            setIsPlaying(false)
            setShowControls(true)
            // Auto play next video if available
            if (currentIndex < videoList.length - 1) {
                setCurrentIndex(prev => prev + 1)
            }
        }

        const handleError = (e: Event) => {
            console.error('❌ VideoSimulation: Video error', e, video.error)

            // Strategy 1: Try fallback if path contains /videos/ (lowercase) -> /Videos/ (uppercase)
            if (!hasRetried && currentVideoPath && currentVideoPath.includes('/videos/')) {
                console.log('🔄 VideoSimulation: Retrying with /Videos/ path...')
                const newPath = currentVideoPath.replace('/videos/', '/Videos/')
                setHasRetried(true)
                setCurrentVideoPath(newPath)
                return
            }

            // Strategy 2: Cache busting (if not already tried)
            // If we have a format error (4) or network error (2), it might be a stale cache
            if (!currentVideoPath?.includes('?retry=') && video.error?.code === 4) {
                console.log('🔄 VideoSimulation: Format error detected. Retrying with cache-busting...')
                const separator = currentVideoPath?.includes('?') ? '&' : '?'
                const newPath = `${currentVideoPath}${separator}retry=${Date.now()}`
                setCurrentVideoPath(newPath)
                return
            }

            setError(true)
            setLoading(false)
        }

        // Clean up old caches (Run once)
        const cleanupOldCaches = async () => {
            const CURRENT_CACHE = 'video-cache-v5'
            if (!('caches' in window)) return
            try {
                const cacheNames = await caches.keys()
                await Promise.all(
                    cacheNames.map(name => {
                        if (name.startsWith('video-cache-') && name !== CURRENT_CACHE) {
                            return caches.delete(name)
                        }
                    })
                )
            } catch (e) {
                console.warn('⚠️ VideoSimulation: Cleanup error:', e)
            }
        }
        cleanupOldCaches()

        // Mobile-Safe Prefetching Strategy:
        // Use <link rel="prefetch"> to let the browser schedule downloads efficiently.
        // This avoids OOM crashes on mobile (which happens with manual fetch().blob() on large files)
        // while still ensuring the Service Worker (CacheFirst) intercepts and caches the streams.
        const prefetchVideos = () => {
            if (videos.length > 0) {
                videos.forEach((v) => {
                    if (v.videoPath && v.videoPath !== currentVideoPath && !v.videoPath.includes('youtube')) {
                        // Check if link already exists
                        if (!document.querySelector(`link[rel="prefetch"][href="${v.videoPath}"]`)) {
                            const link = document.createElement('link')
                            link.rel = 'prefetch'
                            link.as = 'video'
                            link.href = v.videoPath
                            document.head.appendChild(link)
                            console.log('🔗 VideoSimulation: Added prefetch link:', v.videoPath.split('/').pop())
                        }
                    }
                })
            }
        }

        // Delay prefetch slightly to let the main video start first
        const prefetchTimeout = setTimeout(prefetchVideos, 2000)

        video.addEventListener('loadedmetadata', handleLoadedMetadata)
        video.addEventListener('timeupdate', handleTimeUpdate)
        video.addEventListener('ended', handleEnded)
        video.addEventListener('error', handleError)

        // Reset source when path changes
        video.src = currentVideoPath
        video.load()

        return () => {
            clearTimeout(prefetchTimeout)
            video.removeEventListener('loadedmetadata', handleLoadedMetadata)
            video.removeEventListener('timeupdate', handleTimeUpdate)
            video.removeEventListener('ended', handleEnded)
            video.removeEventListener('error', handleError)
        }
    }, [onProgress, currentVideoPath, hasRetried, isVisible])

    const handlePlayPause = () => {
        const video = videoRef.current
        if (!video) return

        if (isPlaying) {
            video.pause()
            setShowControls(true)
            if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current)
        } else {
            video.play().catch(e => console.error("Play failed:", e))
            resetControlsTimeout()
        }
        setIsPlaying(!isPlaying)
    }

    const handleMuteToggle = () => {
        const video = videoRef.current
        if (!video) return

        video.muted = !isMuted
        setIsMuted(!isMuted)
        handleControlsInteraction()
    }

    const handleReset = () => {
        const video = videoRef.current
        if (!video) return

        video.currentTime = 0
        video.pause()
        setIsPlaying(false)
        setCurrentTime(0)
        setShowControls(true)
    }

    const handleFullscreen = () => {
        const video = videoRef.current
        if (!video) return

        // Standard
        if (video.requestFullscreen) {
            video.requestFullscreen().catch(err => {
                console.error("Error attempting to enable fullscreen:", err);
            });
        }
        // iOS Native Video Player
        else if ((video as any).webkitEnterFullscreen) {
            (video as any).webkitEnterFullscreen();
        }
        // Safari (Old) / iOS (Old)
        else if ((video as any).webkitRequestFullscreen) {
            (video as any).webkitRequestFullscreen();
        }
        // Firefox
        else if ((video as any).mozRequestFullScreen) {
            (video as any).mozRequestFullScreen();
        }
        // IE/Edge Legacy
        else if ((video as any).msRequestFullscreen) {
            (video as any).msRequestFullscreen();
        }
        handleControlsInteraction()
    }

    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const video = videoRef.current
        if (!video) return

        const progressBar = e.currentTarget
        const clickPosition = (e.clientX - progressBar.getBoundingClientRect().left) / progressBar.offsetWidth
        video.currentTime = clickPosition * video.duration
        handleControlsInteraction()
    }



    if (!activeVideo.videoPath) {
        return null
    }

    return (
        <div ref={containerRef} className={compact ? "" : "bg-white rounded-xl border border-gray-200 p-2 sm:p-4 my-2 sm:my-4"}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Main Video Area */}
                <div className={`col-span-1 ${videoList.length > 1 ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
                    {/* Video Title & Description (Mobile only or if single video) */}
                    {showInfo && (
                        <div className="mb-3">
                            <h3 className="text-lg font-semibold text-gray-900">{activeVideo.title}</h3>
                            <p className="text-sm text-gray-600">{activeVideo.description}</p>
                        </div>
                    )}

                    {/* Video Container */}
                    <div
                        className="relative bg-black rounded-lg overflow-hidden shadow-lg group"
                        style={{ paddingBottom: activeVideo.videoPath?.includes('youtube') || activeVideo.videoPath?.includes('youtu.be') ? '56.25%' : undefined }}
                        onMouseEnter={() => setShowControls(true)}
                        onMouseLeave={() => isPlaying && setShowControls(false)}
                        onClick={handleControlsInteraction}
                    >
                        {!isVisible ? (
                            // Placeholder while waiting for scroll
                            <div className="w-full aspect-video bg-gray-100 flex items-center justify-center">
                                <span className="text-gray-400 text-sm">Đang tải player...</span>
                            </div>
                        ) : (
                            <>
                                {loading && !activeVideo.videoPath?.includes('youtube') && !activeVideo.videoPath?.includes('youtu.be') && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-10">
                                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
                                    </div>
                                )}

                                {error && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 text-white p-4 z-10">
                                        <AlertCircle className="w-8 h-8 text-yellow-500 mb-2" />
                                        <p className="text-sm font-semibold">Video chưa sẵn sàng</p>
                                        <p className="text-xs text-gray-400 mt-1">{activeVideo.title}</p>
                                    </div>
                                )}

                                {/* Check for YouTube URL */}
                                {(activeVideo.videoPath?.includes('youtube.com') || activeVideo.videoPath?.includes('youtu.be')) ? (
                                    <iframe
                                        className="absolute top-0 left-0 w-full h-full"
                                        src={activeVideo.videoPath.includes('watch?v=')
                                            ? activeVideo.videoPath.replace('watch?v=', 'embed/')
                                            : activeVideo.videoPath.includes('youtu.be')
                                                ? activeVideo.videoPath.replace('youtu.be/', 'youtube.com/embed/')
                                                : activeVideo.videoPath}
                                        title={activeVideo.title}
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                    ></iframe>
                                ) : (
                                    <>
                                        <video
                                            ref={videoRef}
                                            className="w-full aspect-video bg-black cursor-pointer"
                                            playsInline
                                            preload="metadata" // Only load metadata to save bandwidth/memory
                                            controls={false} // Use custom controls
                                            onClick={handleVideoClick}
                                        >
                                            <source src={currentVideoPath} type="video/mp4" />
                                            Trình duyệt của bạn không hỗ trợ video HTML5.
                                        </video>

                                        {/* Big Play Button Overlay */}
                                        {!isPlaying && !loading && !error && (
                                            <div
                                                className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer z-0"
                                                onClick={handleVideoClick}
                                            >
                                                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all transform hover:scale-110">
                                                    <Play className="w-8 h-8 text-white fill-white ml-1" />
                                                </div>
                                            </div>
                                        )}

                                        {/* Custom Controls Overlay */}
                                        {!error && !loading && (
                                            <div
                                                className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}
                                                onClick={(e) => e.stopPropagation()} // Prevent hiding when clicking controls
                                            >
                                                {/* Progress Bar */}
                                                <div
                                                    className="mb-3 h-1.5 bg-gray-600/50 rounded-full cursor-pointer hover:h-2.5 transition-all"
                                                    onClick={handleProgressClick}
                                                >
                                                    <div
                                                        className="h-full bg-blue-500 rounded-full transition-all relative"
                                                        style={{ width: `${(currentTime / duration) * 100}%` }}
                                                    >
                                                        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-sm scale-0 group-hover:scale-100 transition-transform" />
                                                    </div>
                                                </div>

                                                {/* Control Buttons */}
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            onClick={handlePlayPause}
                                                            className="p-2 text-white hover:bg-white/20 rounded-full transition-colors backdrop-blur-sm"
                                                            title={isPlaying ? 'Tạm dừng' : 'Phát'}
                                                        >
                                                            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                                                        </button>

                                                        <button
                                                            onClick={handleReset}
                                                            className="p-2 text-white hover:bg-white/20 rounded-full transition-colors backdrop-blur-sm"
                                                            title="Phát lại từ đầu"
                                                        >
                                                            <RotateCcw className="w-4 h-4" />
                                                        </button>

                                                        <div className="flex items-center group relative">
                                                            <button
                                                                onClick={handleMuteToggle}
                                                                className="p-2 text-white hover:bg-white/20 rounded-full transition-colors backdrop-blur-sm"
                                                                title={isMuted ? 'Bật âm thanh' : 'Tắt âm thanh'}
                                                            >
                                                                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                                                            </button>
                                                        </div>


                                                    </div>

                                                    <button
                                                        onClick={handleFullscreen}
                                                        className="p-2 text-white hover:bg-white/20 rounded-full transition-colors backdrop-blur-sm"
                                                        title="Toàn màn hình"
                                                    >
                                                        <Maximize className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Playlist Sidebar */}
                {videoList.length > 1 && (
                    <div className="col-span-1 lg:h-full lg:max-h-[500px] flex flex-col bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                        <div className="p-3 bg-white border-b border-gray-200 flex items-center justify-between">
                            <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                                <List className="w-4 h-4" />
                                Danh sách mô phỏng ({videoList.length})
                            </h4>
                        </div>
                        <div className="flex-1 overflow-y-auto p-2 space-y-2">
                            {videoList.map((video, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentIndex(index)}
                                    className={`w-full text-left p-3 rounded-lg transition-all border ${index === currentIndex
                                        ? 'bg-blue-50 border-blue-200 shadow-sm'
                                        : 'bg-white border-transparent hover:bg-gray-100'
                                        }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`mt-1 w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${index === currentIndex ? 'text-blue-500' : 'text-gray-300'
                                            }`}>
                                            {index === currentIndex ? (
                                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                            ) : (
                                                <span className="text-xs font-medium">{index + 1}</span>
                                            )}
                                        </div>
                                        <div>
                                            <h5 className={`text-sm font-medium line-clamp-2 ${index === currentIndex ? 'text-blue-700' : 'text-gray-700'
                                                }`}>
                                                {video.title}
                                            </h5>
                                            {video.description && (
                                                <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                                                    {video.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default VideoSimulation
